import { CrossSectionCanvas } from '@pages/cross-section/shared/CrossSectionCanvas';
import { LabelService } from '@shared/services/LabelService';
import { WindowManager } from '@shared/ui-react/FloatingWindow';
import { showTekPointSoilSurvey } from '@pages/soil-survey/soil-survey-main';
import { SURVEY_TYPE_TITLES } from '@pages/soil-survey/soil-survey-constants';
import Konva from 'konva';
import React from 'react';
import { getTekPointSvg } from '@shared/openlayers/points/TekPointsLayer';

function drawModel(opts: {
    model: any;
    csCanvas: CrossSectionCanvas;
    hiddenLineIds: Array<string | number>;
    onClickCallback: (pipe, x, y) => void;
    type: 'model' | 'terrainModel';
}) {
    const { model, csCanvas, onClickCallback, hiddenLineIds } = opts;
    const center = csCanvas.getCenter();
    const group = new Konva.Group({
        id: 'model_' + model.id,
    });

    const canvasOptions = csCanvas.canvasOptions;
    const lowestPoint = csCanvas.lowestPoint;

    model.lines.forEach(modelLine => {
        if (hiddenLineIds.includes(modelLine.id)) {
            return;
        }
        // These props are usually empty. Hide them unless some of them has some reasonable value
        const name = modelLine.name ?? '';
        const surfaceCode = typeof modelLine.surfaceCode !== 'undefined' && modelLine.surfaceCode !== null ? modelLine.surfaceCode : '';
        const terrainCode = typeof modelLine.terrainCode !== 'undefined' && modelLine.terrainCode !== null ? modelLine.terrainCode : '';
        let props;
        if (name.length > 0 || terrainCode.length > 0 || surfaceCode.length > 0) {
            props = { name: name, terrainCode: terrainCode, surfaceCode: surfaceCode };
        }

        modelLine.vectors.forEach(vector => {
            if (vector.points.length < 2) {
                return true;
            }

            const x0 = center + vector.points[0].x * canvasOptions.x_scale;
            const y0 = csCanvas.height - canvasOptions.bottom_margin + lowestPoint * canvasOptions.y_scale - vector.points[0].y * canvasOptions.y_scale + csCanvas.panY;
            const x1 = center + vector.points[1].x * canvasOptions.x_scale;
            const y1 = csCanvas.height - canvasOptions.bottom_margin + lowestPoint * canvasOptions.y_scale - vector.points[1].y * canvasOptions.y_scale + csCanvas.panY;

            if (x0 < canvasOptions.side_margin / 2 && x1 < canvasOptions.side_margin / 2) {
                return;
            } else if (x0 > csCanvas.width - canvasOptions.side_margin / 2 && x1 > csCanvas.width - canvasOptions.side_margin / 2) {
                return;
            } else if (y0 < canvasOptions.top_margin && y1 < canvasOptions.top_margin) {
                return;
            } else if (y0 > csCanvas.height - canvasOptions.bottom_margin && y1 > csCanvas.height - canvasOptions.bottom_margin) {
                return;
            }

            const xLen = x1 - x0;
            const yLen = y1 - y0;
            const lineLen = Math.sqrt(xLen * xLen + yLen * yLen);
            if (lineLen < 2) {
                return;
            }

            const line = new Konva.Line({
                name: 'model_' + model.modelId,
                points: [x0, y0, x1, y1],
                stroke: getRgb(model.color),
                strokeWidth: model.strokeWidth,
                lineCap: 'round',
                lineJoin: 'round',
                listening: false,
            });

            // Set tooltip line a bit shorter than the real line -> from +10% start to -10% from end
            if (lineLen > 10) {
                const corx = xLen / 10;
                const cory = yLen / 10;

                const tooltipLine = new Konva.Line({
                    name: 'tooltipline_model',
                    points: [x0 + corx, y0 + cory, x1 - corx, y1 - cory],
                    stroke: getRgb(model.color),
                    strokeWidth: 4,
                    lineCap: 'round',
                    lineJoin: 'round',
                    listening: true,
                    opacity: 0,
                });
                tooltipLine.on('click tap', function () {
                    line.strokeWidth(line.strokeWidth() + 2);
                    showLineTooltip(opts.model.name, name, x0, y0, csCanvas, true);
                });
                tooltipLine.on('mouseover', function () {
                    line.strokeWidth(line.strokeWidth() + 2);
                    showLineTooltip(opts.model.name, name, x0, y0, csCanvas, false);
                });
                tooltipLine.on('mouseout', function () {
                    line.strokeWidth(line.strokeWidth() - 2);
                    csCanvas.clearHoverTooltips();
                    csCanvas.contentLayer.draw();
                });
                group.add(tooltipLine);
            }
            const vertex0Hitbox = new Konva.Circle({
                name: 'model_' + model.modelId,
                x: x0,
                y: y0,
                stroke: 'transparent',
                radius: 5,
            });
            const vertex1Hitbox = new Konva.Circle({
                name: 'model_' + model.modelId,
                x: x1,
                y: y1,
                stroke: 'transparent',
                radius: 5,
            });

            if (opts.type === 'terrainModel') {
                line.dash([9, 9]);
            }
            vertex0Hitbox.on('click tap', function () {
                onClickCallback(vector.points[0], x0, y0);
            });
            vertex1Hitbox.on('click tap', function () {
                onClickCallback(vector.points[1], x1, y1);
            });

            vertex0Hitbox.on('mouseover', function () {
                showVertexTooltip(vector.points[0], x0, y0, model.name, modelLine.name, props, csCanvas);
                csCanvas.drawSnapCursor(true, x0, y0);
            });
            vertex1Hitbox.on('mouseover', function () {
                showVertexTooltip(vector.points[1], x1, y1, model.name, modelLine.name, props, csCanvas);
                csCanvas.drawSnapCursor(true, x1, y1);
            });

            vertex0Hitbox.on('mouseout', csCanvas.clearHoverTooltips);
            vertex1Hitbox.on('mouseout', csCanvas.clearHoverTooltips);
            group.add(vertex0Hitbox);
            group.add(vertex1Hitbox);
            group.add(line);
        });
    });

    model.points.forEach(modelPoint => {
        const x = csCanvas.metersToScreenCoordsX(modelPoint.point.x);
        const y = csCanvas.metersToScreenCoordsY(modelPoint.point.y);

        if (x < canvasOptions.side_margin / 2 || x > csCanvas.width - canvasOptions.side_margin / 2) {
            return;
        }
        if (y < canvasOptions.top_margin || y > csCanvas.height - canvasOptions.bottom_margin) {
            return;
        }

        const color = modelPoint.color || model.color || '0,0,0';

        let shape;
        if (modelPoint.props && modelPoint.props.tutkimustapa) {
            const tt = modelPoint.props.tutkimustapa.split(/\s+/)[0];
            const svgPath = getTekPointSvg(tt);

            const imageObj = new Image();
            imageObj.onload = function () {
                const imageShape = new Konva.Image({
                    name: 'model_' + model.modelId,
                    image: imageObj,
                    x: x - 7,
                    y: y - 7,
                    width: 14,
                    height: 14,
                    listening: false,
                });

                group.add(imageShape);
                csCanvas.contentLayer.draw();
            };
            imageObj.src = svgPath;

            const biggestEntry = Object.entries(modelPoint.props)
                .filter(([key, value]) => {
                    if (isNaN(Number(key)) || key.trim() === '') return false;
                    const firstPart = String(value).split(/\s+/)[0];
                    return !isNaN(Number(firstPart)) && firstPart.trim() !== '';
                })
                .reduce((max, [key, value]) => (Number(key) > max.key ? { key: Number(key), value: parseFloat(String(value).split(/\s+/)[0]) } : max), {
                    key: -Infinity,
                    value: 0,
                });

            const rect = new Konva.Rect({
                x: x - 5,
                y: Math.min(y, csCanvas.metersToScreenCoordsY(modelPoint.point.y - biggestEntry.value)),
                width: 10,
                height: Math.abs(csCanvas.metersToScreenCoordsY(modelPoint.point.y - biggestEntry.value) - y),
                fill: 'white',
                stroke: getRgb(color),
                strokeWidth: 1,
                listening: false,
            });

            // Create a hitbox for the rectangle
            const rectHitbox = new Konva.Rect({
                name: 'model_' + model.modelId,
                x: x - 8,
                y: Math.min(y, csCanvas.metersToScreenCoordsY(modelPoint.point.y - biggestEntry.value)) - 2,
                width: 12,
                height: Math.abs(csCanvas.metersToScreenCoordsY(modelPoint.point.y - biggestEntry.value) - y) + 4,
                fill: 'transparent',
                listening: true,
            });

            // Add the same event handlers as the circle hitbox
            rectHitbox.on('click tap', function (e) {
                e.evt.stopPropagation();
                e.evt.preventDefault();
                csCanvas.canvasWrapperEl.style.cursor = 'default';
                csCanvas.mouseDown = false;
                showTekPointWindow(modelPoint.point, modelPoint.props, opts.model.name);
            });

            rectHitbox.on('mouseover', function () {
                csCanvas.canvasWrapperEl.style.cursor = 'pointer';
            });

            rectHitbox.on('mouseout', function () {
                csCanvas.canvasWrapperEl.style.cursor = 'default';
                csCanvas.clearHoverTooltips();
            });

            group.add(rect);
            group.add(rectHitbox);
            shape = null;
        } else {
            shape = new Konva.Circle({
                name: 'model_' + model.modelId,
                x: x,
                y: y,
                stroke: getRgb(color),
                fill: getRgb(color),
                radius: 2,
                listening: false,
            });
        }

        const hitbox = new Konva.Circle({
            name: 'model_' + model.modelId,
            x: x,
            y: y,
            stroke: 'transparent',
            radius: 5,
        });

        hitbox.on('click tap', function (e) {
            if (modelPoint.props && modelPoint.props.tutkimustapa) {
                e.evt.stopPropagation();
                e.evt.preventDefault();
                csCanvas.canvasWrapperEl.style.cursor = 'default';
                csCanvas.mouseDown = false;

                showTekPointWindow(modelPoint.point, modelPoint.props, opts.model.name);
            } else {
                onClickCallback(modelPoint.point, x, y);
                showVertexTooltip(modelPoint.point, x, y, opts.model.name, modelPoint.name, modelPoint.props, csCanvas, true);
            }
        });

        hitbox.on('mouseover', function () {
            if (modelPoint.props && modelPoint.props.tutkimustapa) {
                csCanvas.canvasWrapperEl.style.cursor = 'pointer';
            } else {
                showVertexTooltip(modelPoint.point, x, y, opts.model.name, modelPoint.name, modelPoint.props, csCanvas, false);
            }
        });

        hitbox.on('mouseout', function () {
            // Reset cursor
            csCanvas.canvasWrapperEl.style.cursor = 'default';
            csCanvas.clearHoverTooltips();
        });

        // Add the hitbox first, then shape if it exists
        group.add(hitbox);
        if (shape) {
            group.add(shape);
        }
    });

    csCanvas.contentLayer.add(group);
}

function showLineTooltip(modelName: string, lineName: string, x: number, y: number, csCanvas: CrossSectionCanvas, sticky = false) {
    const text = [modelName, lineName].filter(Boolean).join('\n');
    csCanvas.drawTooltipAtMousePosition(text, -25, sticky);
}

function showVertexTooltip(point: any, x: number, y: number, modelName: string, lineName: string, props: Record<string, any>, csCanvas: CrossSectionCanvas, sticky = false) {
    const text = [
        modelName,
        lineName,
        kuura.getMessage('js.cross_section.b') + ': ' + point.x.toFixed(2),
        kuura.getMessage('js.cross_section.z') + ': ' + point.y.toFixed(2),
    ].filter(Boolean);

    if (props && props.tutkimustapa) {
        if (sticky) {
            showTekPointWindow(point, props, modelName);
        }
        return;
    }

    if (props) {
        if (Object.keys(props).length > 0) {
            text.push('\n');
        }
        Object.entries(props).forEach(([key, val]) => {
            text.push(key + ': ' + val);
        });
    }

    csCanvas.tooltipLayer.add(
        new Konva.Rect({
            x: x - 4,
            y: y - 4,
            width: 9,
            height: 9,
            stroke: 'gray',
            strokeWidth: 1,
            listening: false,
        })
    );
    csCanvas.drawTooltip(x, y - 15, text.join('\n'), sticky);
}

function showTekPointWindow(point: any, props: Record<string, any>, modelName: string) {
    const htmlContent = LabelService.getHTMLLabelForTekPoint({ ...point, props });

    const tekPointButton = (
        <div className='tek-heading-container' style={{ marginBottom: '10px' }}>
            <h3
                className='tek-heading'
                style={{
                    cursor: 'pointer',
                    display: 'inline-block',
                    color: '#0066cc',
                    margin: '0 0 10px 0',
                }}
                onClick={() => handleTekPointClick(props)}
            >
                Pohjatutkimus
                <i className='iki iki-external' style={{ fontSize: '0.8em', marginLeft: '5px' }}></i>
            </h3>
        </div>
    );

    WindowManager.open({
        group: 'tekpoint',
        id: `${point.x}_${point.y}`,
        showCloseButton: true,
        resize: 'both',
        allowMultiple: true,
        defaultSizing: {
            width: 400,
            height: 600,
        },
        body: (
            <div style={{ padding: '10px', overflow: 'auto' }}>
                {tekPointButton}
                <div className='tekpoint-details' dangerouslySetInnerHTML={{ __html: htmlContent }} />
            </div>
        ),
    });
}

function handleTekPointClick(pointData: any) {
    const tt = pointData['tutkimustapa'];
    if (!tt) {
        console.log('No TT found in point data');
        return;
    }

    const parsedValue = tt.split(/[\s-]/)[0];
    const title = SURVEY_TYPE_TITLES[parsedValue];
    if (title) {
        showTekPointSoilSurvey({
            ...pointData,
            title,
        });
    } else {
        console.log('Unknown TT type:', parsedValue);
    }
}

function drawBorder(border: any, csCanvas: CrossSectionCanvas) {
    const point = border.point;
    const center = csCanvas.getCenter();
    const canvasOptions = csCanvas.canvasOptions;
    const x = center + point.x * canvasOptions.x_scale;
    const y0 = canvasOptions.top_margin;
    const y1 = csCanvas.height - canvasOptions.bottom_margin;

    const line = new Konva.Line({
        name: 'model_' + point.id,
        points: [x, y0, x, y1],
        stroke: getRgb(border.color),
        strokeWidth: 1,
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
    });

    csCanvas.contentLayer.add(line);
}

const getRgb = (color: string) => 'rgb(' + color + ')';

export const CrossSectionModel = {
    drawModel,
    drawBorder,
};
